/**
 * Utility functions for occupation slug generation and URL handling
 */

/** Normalize SOC codes for comparison (dots vs dashes, whitespace). */
export function normalizeSocCode(code: string): string {
  if (!code) return code;
  const trimmed = code.trim().replace(/\./g, '-');
  const match = trimmed.match(/^(\d{2})[.-]?(\d{4})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }
  return trimmed;
}

/**
 * Creates a URL-friendly slug from an occupation title
 */
export function createOccupationSlug(occupationTitle: string): string {
  if (!occupationTitle) return '';
  
  return occupationTitle
    .toLowerCase()
    .trim()
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Replace spaces with hyphens
    .replace(/\s/g, '-')
    // Remove special characters except hyphens
    .replace(/[^a-z0-9-]/g, '')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
}

/**
 * Generates the salary page URL for an occupation
 */
export function generateSalaryPageUrl(
  occupation: {
    title?: string;
    occ_title?: string;
    soc?: string;
    soc_code?: string;
    slug?: string;
  },
  location: string = 'united-states'
): string {
  // Use existing slug if available
  let slug = occupation.slug;
  
  // If no slug, create one from title
  if (!slug) {
    const title = occupation.title || occupation.occ_title || '';
    slug = createOccupationSlug(title);
  }
  
  // If we have a SOC code but no slug from title, use SOC code in slug
  if (!slug && (occupation.soc || occupation.soc_code)) {
    const socCode = occupation.soc || occupation.soc_code || '';
    const title = occupation.title || occupation.occ_title || '';
    if (socCode && title) {
      slug = `${socCode.toLowerCase()}-${createOccupationSlug(title)}`;
    } else if (socCode) {
      slug = socCode.toLowerCase().replace(/[^a-z0-9]/g, '-');
    }
  }
  
  if (!slug) {
    console.warn('Could not generate slug for occupation:', occupation);
    return '/';
  }
  
  // Clean up location slug
  const locationSlug = location
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '') || 'united-states';
  
  // Add trailing slash for SEO consistency (except root)
  return `/salary/${slug}/${locationSlug}/`;
}

/**
 * Extracts location context from current URL or page props
 */
export function extractLocationContext(
  currentPath?: string,
  defaultLocation: string = 'united-states'
): string {
  if (!currentPath) return defaultLocation;
  
  // Extract location from salary page URLs like /salary/occupation/location
  const salaryMatch = currentPath.match(/\/salary\/[^/]+\/([^/?]+)/);
  if (salaryMatch) {
    return salaryMatch[1];
  }
  
  // Extract from best-states URLs like /best-states/occupation
  const bestStatesMatch = currentPath.match(/\/best-states\/([^/?]+)/);
  if (bestStatesMatch) {
    return 'united-states'; // Best states always compares nationally
  }
  
  // Extract from no-degree-jobs state pages like /no-degree-jobs/california
  const noDegreeMatch = currentPath.match(/\/no-degree-jobs\/([^/?]+)/);
  if (noDegreeMatch && noDegreeMatch[1] !== 'united-states') {
    return noDegreeMatch[1];
  }
  
  return defaultLocation;
}

/**
 * Validates if an occupation object has the required data for linking
 */
export function canLinkToSalary(occupation: unknown): boolean {
  if (!occupation) return false;
  
  // Check if we have a title
  const hasTitle = !!(occupation.title || occupation.occ_title);
  if (!hasTitle) return false;
  
  // Check if we have an identifier (soc, soc_code, slug, or we can generate one from title)
  const hasIdentifier = !!(
    occupation.soc || 
    occupation.soc_code || 
    occupation.slug ||
    occupation.title ||
    occupation.occ_title
  );
  
  return hasIdentifier;
}